<script lang="ts" setup>
import { GAnalyticsEvent } from '@jaaluu/interfaces/GAnalyticEvent';
import stores from '@jaaluu/stores';
import { t } from '@shared/i18n';
import {
  CategoryType,
  GoogleRssTopic,
  Language,
  LanguageLevel,
  MainCategories,
  WordInfoService
} from '@shared/openapi/jaaluu-api';
import sharedStores from '@shared/shared-stores';
import { computed, onMounted, ref, Ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Dialog from '../Dialog.vue';
import SelectedLanguages from '../SelectedLanguages.vue';
import TeacherChat from '../TeacherChat.vue';

const router = useRouter();

const PROGRESS_HIGHT = 2;

const { animate } = sharedStores.animate.funcs;
const { openTooltip } = stores.tooltip.funcs;
const { TRANS } = stores.translate.consts;
const { CAT_TYPE_ICON_NAMES } = stores.category.consts;
const { openCategory: _openCategory } = stores.category.funcs;
const { allFavWordsLang, allFavUrlsLang, allFavWordmapsLang, categoryStates } =
  stores.favorites.state;
const { langSettingsOpen, levelSettingsOpen, audioVideoSettingsIsOpen, themeSettingsIsOpen } =
  stores.settings.state;
const { cookieDialogIsOpen, cookieDialogFromSettings } = stores.cookies.state;
const { langForeign, langLevel, langNative, langOptions } = stores.language.state;
const { ytChannels } = stores.video.state;
const { courseStatus } = stores.course.state;
const {
  getLastGrammarChapter,
  loadGrammarMenu,
  flattenChapters,
  getLastVocabularyIndex,
  loadVocabularyTotal,
  getVocabularyTotal,
  getLastJokeIndex,
  loadJokeTotal,
  getJokeTotal
} = stores.grammar.funcs;
const { getMainProgress } = stores.progress.funcs;
const { courseProgress } = stores.courseProgress.state;
const { grammarProgress } = stores.grammarProgress.state;
const { sendEvent } = stores.gAnalytics.funcs;
const { handleDefaultError } = stores.error.funcs;

const translatorDialogIsOpen = ref(false);
const teacherChatIsOpen = ref(false);
const toTranslate = ref('');
const _langForeign: Ref<Language | null> = ref(null);
const _langNative: Ref<Language | null> = ref(null);
const _langForeignTrans: Ref<Language | null> = ref(null);
const _langNativeTrans: Ref<Language | null> = ref(null);
const menuOpen = ref(false);
const checkingWord = ref(false);
const invalidWord: Ref<{ word: string; similarWords: string[] } | null> = ref(null);

onMounted(() => {
  setLang();
  loadVocabularyTotal();
  loadJokeTotal();
});

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const trackMenuClick = (item: string, action: () => void) => {
  sendEvent(GAnalyticsEvent.CLICK_SIDE_MENU_ITEM, { item });
  action();
};

const openUrl = (url: string) => {
  router.push(url);
  closeMenu();
};

const openCategory = (categoryType: string) => {
  if (!langForeign.value || !langNative.value) return;
  _openCategory(router, categoryType, langForeign.value, langNative.value, categoryStates.value);
  closeMenu();
};

const openLangSettings = () => {
  langSettingsOpen.value = true;
  closeMenu();
};

const openLevelSettings = () => {
  levelSettingsOpen.value = true;
  closeMenu();
};

const openAudioVideoSettings = () => {
  audioVideoSettingsIsOpen.value = true;
  closeMenu();
};
const openThemeSettings = () => {
  themeSettingsIsOpen.value = true;
  closeMenu();
};
const openCookieSettings = () => {
  cookieDialogFromSettings.value = true;
  cookieDialogIsOpen.value = true;
  closeMenu();
};

const closeMenu = () => {
  menuOpen.value = false;
};

const openCourse = () => {
  console.log('Open course from side menu');
  router.push({ name: 'course-status', params: { langForeign: langForeign.value } });
  courseStatus.value.updateIsActive = false;
  menuOpen.value = false;
};

const openGrammar = async () => {
  if (!langForeign.value) return;
  const lastChapter = getLastGrammarChapter();
  if (lastChapter) {
    openUrl(`/${langForeign.value}/grammar/chapter/${lastChapter}`);
    return;
  }
  await loadGrammarMenu();
  const chapters = flattenChapters();
  if (chapters.length > 0) {
    openUrl(`/${langForeign.value}/grammar/chapter/${chapters[0]}`);
  } else {
    openUrl(`/${langForeign.value}/grammar`);
  }
};

const categoryProgressPct = (categoryType: CategoryType) => {
  const mainCats = Object.values(MainCategories) as MainCategories[];
  const total = mainCats.reduce((sum, mc) => sum + getMainProgress(mc, categoryType).pct, 0);
  return Math.round(total / mainCats.length);
};

const writeProgress = computed(() => categoryProgressPct(CategoryType.WRITE));
const conversationsProgress = computed(() => categoryProgressPct(CategoryType.CONVERSATIONS));
const sayingsProgress = computed(() => categoryProgressPct(CategoryType.SAYINGS));
const simpleTextsProgress = computed(() => categoryProgressPct(CategoryType.SIMPLE_TEXTS));
const phrasesProgress = computed(() => categoryProgressPct(CategoryType.PHRASES));
const courseProgressPct = computed(() => courseProgress.value.overallProgress);
const grammarProgressPct = computed(() => grammarProgress.value.overallProgress);

const vocabularyProgress = computed(() => {
  const total = getVocabularyTotal();
  const lastIndex = getLastVocabularyIndex();
  if (!total || lastIndex === undefined) return 0;
  return Math.round(((lastIndex + 1) / total) * 100);
});
const jokesProgress = computed(() => {
  const total = getJokeTotal();
  const lastIndex = getLastJokeIndex();
  if (!total || lastIndex === undefined) return 0;
  return Math.round(((lastIndex + 1) / total) * 100);
});

const setLang = () => {
  if (!langForeign.value || !langNative.value) {
    console.error(
      'Not all languages set. Lang foreign and native',
      langForeign.value,
      langNative.value
    );
    return;
  }
  _langForeign.value = langForeign.value;
  _langNative.value = langNative.value;
  _langForeignTrans.value = langForeign.value;
  _langNativeTrans.value = langNative.value;
};

const routeToHome = () => {
  courseStatus.value.isActive = false;
  router.push(`/${langForeign.value ? langForeign.value : 'welcome'}`);
  menuOpen.value = false;
};

const getTranslation = async (event: any) => {
  if (!toTranslate.value) return;
  invalidWord.value = null;
  const word = toTranslate.value.trim();
  const isSingleWord = word.split(' ').length === 1;
  if (isSingleWord && _langForeignTrans.value) {
    checkingWord.value = true;
    try {
      const res: any = await WordInfoService.getWordValidationV1WordInfoWordValidationGet({
        query: { q: word, foreign: _langForeignTrans.value }
      });
      if (!res.data.is_valid) {
        invalidWord.value = { word, similarWords: res.data.similar_words ?? [] };
        checkingWord.value = false;
        return;
      }
    } catch (error) {
      handleDefaultError(error);
    }
    checkingWord.value = false;
  }
  openTooltip(
    event,
    toTranslate.value,
    null,
    toTranslate.value.split(' ').length > 1 ? true : false,
    _langNativeTrans.value === langForeign.value ? true : false
  );
  translatorDialogIsOpen.value = false;
};

const selectSimilarWord = (word: string, event: any) => {
  toTranslate.value = word;
  getTranslation(event);
};

const switchLangs = () => {
  _langNativeTrans.value =
    _langNativeTrans.value === langForeign.value ? langNative.value : langForeign.value;
  _langForeignTrans.value =
    _langForeignTrans.value === langNative.value ? langForeign.value : langNative.value;
};

const openTranslateDialog = () => {
  translatorDialogIsOpen.value = true;
  menuOpen.value = false;
};

watch(toTranslate, () => {
  invalidWord.value = null;
});

watch(
  () => allFavWordsLang.value.length,
  () => {
    animate('#fav-badge');
  }
);
watch(
  () => allFavUrlsLang.value.length,
  () => {
    animate('#fav-badge');
  }
);
watch(
  () => allFavWordmapsLang.value.length,
  () => {
    animate('#fav-badge');
  }
);
watch(
  () => langForeign.value,
  () => {
    setLang();
    loadVocabularyTotal();
    loadJokeTotal();
  }
);
watch(
  () => langNative.value,
  () => {
    setLang();
  }
);
watch(
  () => langSettingsOpen.value,
  () => {
    setLang();
  }
);
</script>
<template>
  <div class="d-flex align-center" v-if="ytChannels">
    <v-btn icon="mdi-menu" @click="() => toggleMenu()"></v-btn>
    <div
      class="menu"
      :class="{
        active: menuOpen
      }"
    >
      <v-list color="green" density="compact" v-if="langLevel">
        <div class="d-flex justify-space-between align-center mb-4">
          <div class="ml-4 subtitle">{{ t('app_bar.menu') }}</div>
          <v-btn size="small" variant="text" icon="mdi-close" @click="() => (menuOpen = false)" />
        </div>

        <div class="d-flex align-center justify-space-around mb-12">
          <v-btn class="d-flex grab" @click="() => openLevelSettings()">
            <v-icon
              icon="mdi-podium-bronze"
              v-if="langLevel === LanguageLevel.A1_A2"
              class="mr-1 mt-n1"
            />
            <v-icon
              icon="mdi-podium-silver"
              v-if="langLevel === LanguageLevel.B1_B2"
              class="mr-1 mt-n1"
            />
            <v-icon
              icon="mdi-podium-gold"
              v-if="langLevel === LanguageLevel.C1_C2"
              class="mr-1 mt-n1"
            />
            <div class="capitalize ml-1">{{ langLevel.toLocaleLowerCase() }}</div>
          </v-btn>
          <v-btn @click="() => openLangSettings()">
            <SelectedLanguages />
          </v-btn>
        </div>

        <v-list-item
          prepend-icon="mdi-home"
          :title="t('general.home')"
          :value="t('general.home')"
          @click="() => trackMenuClick('home', routeToHome)"
        ></v-list-item>

        <v-list-item
          v-if="langForeign && langNative && langLevel === LanguageLevel.C1_C2"
          :prepend-icon="CAT_TYPE_ICON_NAMES.write"
          :title="TRANS[langForeign].general.write + ' - ' + TRANS[langNative].general.write"
          :value="TRANS[langForeign].general.write"
          @click="() => trackMenuClick('write', () => openCategory('write'))"
          ><div class="d-flex align-center menu-progress" v-if="writeProgress > 0">
            <v-progress-linear
              :model-value="writeProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ writeProgress }}%</span>
          </div></v-list-item>

        <!-- <v-list-group
          :value="'videos-news'"
          v-if="langForeign && langLevel === LanguageLevel.C1_C2"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              prepend-icon="mdi-youtube"
              v-bind="props"
              :title="TRANS[langForeign].general.videos + ' - ' + TRANS[langForeign].general.feeds"
              ></v-list-item>
          </template>
          <v-list-item
            v-if="ytChannels"
            v-for="(channel, i) in ytChannels[langForeign].slice(0, 2)"
            :key="i"
            :title="channel.title"
            :value="channel.title"
            @click="() => openUrl(`/${langForeign}/videos/${channel.channel_id}`)"
          ></v-list-item>
        </v-list-group> -->

        <v-list-item
          v-if="langForeign && langNative && langLevel === LanguageLevel.C1_C2"
          prepend-icon="mdi-book-open-variant-outline"
          :title="TRANS[langForeign].general.books + ' - ' + TRANS[langNative].general.books"
          :value="TRANS[langForeign].general.books"
          @click="() => trackMenuClick('books', () => openUrl(`/${langForeign}/books`))"
          ></v-list-item>

        <v-list-item
          v-if="
            langForeign &&
            langNative &&
            [LanguageLevel.C1_C2, LanguageLevel.B1_B2].includes(langLevel)
          "
          prepend-icon="mdi-account-voice"
          :title="
            TRANS[langForeign].general.live_conversations +
            ' - ' +
            TRANS[langNative].general.live_conversations
          "
          :value="TRANS[langForeign].general.live_conversations"
          @click="
            () =>
              trackMenuClick('live_conversations', () =>
                openUrl(`/${langForeign}/live-conversations`)
              )
          "
        ></v-list-item>

        <v-list-item
          v-if="
            langForeign &&
            langNative &&
            [LanguageLevel.C1_C2, LanguageLevel.B1_B2].includes(langLevel)
          "
          :prepend-icon="CAT_TYPE_ICON_NAMES.conversations"
          :title="
            TRANS[langForeign].general.conversations +
            ' - ' +
            TRANS[langNative].general.conversations
          "
          :value="TRANS[langForeign].general.conversations"
          @click="() => trackMenuClick('conversations', () => openCategory('conversations'))"
          ><div class="d-flex align-center menu-progress" v-if="conversationsProgress > 0">
            <v-progress-linear
              :model-value="conversationsProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ conversationsProgress }}%</span>
          </div></v-list-item>


        <v-list-item
          v-if="
            langForeign &&
            langNative &&
            [LanguageLevel.C1_C2, LanguageLevel.B1_B2].includes(langLevel)
          "
          prepend-icon="mdi-wikipedia"
          :title="
            TRANS[langForeign].general.wikipedia + ' - ' + TRANS[langNative].general.wikipedia
          "
          :value="'Wikipedia'"
          @click="() => trackMenuClick('wikipedia', () => openUrl(`/${langForeign}/wikipedia`))"
          ></v-list-item>

        <v-list-group
          :value="t('general.feeds')"
          v-if="
            langForeign &&
            langNative
          "
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              prepend-icon="mdi-newspaper-variant-outline"
              v-bind="props"
              :title="TRANS[langForeign].general.feeds + ' - ' + TRANS[langNative].general.feeds"
            >
            </v-list-item>
          </template>
          <v-list-item
            v-for="(topicKey, i) in GoogleRssTopic"
            :key="i"
            :title="
              TRANS[langForeign].feed_topics[topicKey] +
              ' - ' +
              TRANS[langNative].feed_topics[topicKey]
            "
            :value="TRANS[langForeign].feed_topics[topicKey]"
            @click="
              () => trackMenuClick(`feeds_${topicKey}`, () => openUrl(`/${langForeign}/feeds/${topicKey}`))
            "
          >
          </v-list-item>
        </v-list-group>

        <!-- <v-list-item
    v-if="langForeign"
    prepend-icon="mdi-message"
    :title="TRANS[langForeign].chat.chat"
    :value="TRANS[langForeign].chat.chat"
    @click="() => openUrl(`/${langForeign.value}/chat`)"
    ><v-progress-linear
      :model-value="50"
      color="green"
      :height="PROGRESS_HIGHT"
      class="difficult-progress"
      size="x-small"
  /></v-list-item> -->

        <!--  <v-list-group
          :value="'videos-kids'"
          v-if="langForeign && [LanguageLevel.C1_C2, LanguageLevel.B1_B2].includes(langLevel)"
        >
          <template v-slot:activator="{ props }">
            <v-list-item
              prepend-icon="mdi-youtube"
              v-bind="props"
              :title="TRANS[langForeign].general.videos + ' - ' + TRANS[langForeign].general.kids"
              ></v-list-item>
          </template>
          <v-list-item
            v-if="ytChannels"
            v-for="(channel, i) in ytChannels[langForeign].slice(2, 4)"
            :key="i"
            :title="channel.title"
            :value="channel.title"
            @click="() => openUrl(`/${langForeign}/videos/${channel.channel_id}`)"
          ></v-list-item>
        </v-list-group> -->

        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.B1_B2].includes(langLevel)"
          prepend-icon="mdi-teddy-bear"
          :title="
            TRANS[langForeign].general.kids_lexicon + ' - ' + TRANS[langNative].general.kids_lexicon
          "
          :value="TRANS[langForeign].general.kids_lexicon"
          @click="() => trackMenuClick('kids_lexicon', () => openUrl(`/${langForeign}/kids_lexicon`))"
        ></v-list-item>

        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.B1_B2].includes(langLevel)"
          :prepend-icon="CAT_TYPE_ICON_NAMES.sayings"
          :title="TRANS[langForeign].category.sayings + ' - ' + TRANS[langNative].category.sayings"
          :value="TRANS[langForeign].category.sayings"
          @click="() => trackMenuClick('sayings', () => openCategory('sayings'))"
        ><div class="d-flex align-center menu-progress" v-if="sayingsProgress > 0">
            <v-progress-linear
              :model-value="sayingsProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ sayingsProgress }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.B1_B2].includes(langLevel)"
          :prepend-icon="CAT_TYPE_ICON_NAMES.simple_texts"
          :title="
            TRANS[langForeign].category.simple_texts +
            ' - ' +
            TRANS[langNative].category.simple_texts
          "
          :value="TRANS[langForeign].category.simple_texts"
          @click="() => trackMenuClick('simple_texts', () => openCategory('simple_texts'))"
        ><div class="d-flex align-center menu-progress" v-if="simpleTextsProgress > 0">
            <v-progress-linear
              :model-value="simpleTextsProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ simpleTextsProgress }}%</span>
          </div></v-list-item>
        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.A1_A2].includes(langLevel)"
          :prepend-icon="CAT_TYPE_ICON_NAMES.course"
          :title="TRANS[langForeign].category.course + ' - ' + TRANS[langNative].category.course"
          :value="TRANS[langForeign].category.course"
          @click="() => trackMenuClick('course', openCourse)"
        ><div class="d-flex align-center menu-progress" v-if="courseProgressPct > 0">
            <v-progress-linear
              :model-value="courseProgressPct"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ courseProgressPct }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.A1_A2].includes(langLevel)"
          :prepend-icon="CAT_TYPE_ICON_NAMES.phrases"
          :title="TRANS[langForeign].category.phrases + ' - ' + TRANS[langNative].category.phrases"
          :value="TRANS[langForeign].category.phrases"
          @click="() => trackMenuClick('phrases', () => openCategory('phrases'))"
        ><div class="d-flex align-center menu-progress" v-if="phrasesProgress > 0">
            <v-progress-linear
              :model-value="phrasesProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ phrasesProgress }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.A1_A2, LanguageLevel.B1_B2].includes(langLevel)"
          prepend-icon="mdi-format-text"
          :title="TRANS[langForeign].general.grammar + ' - ' + TRANS[langNative].general.grammar"
          :value="TRANS[langForeign].general.grammar"
          @click="() => trackMenuClick('grammar', openGrammar)"
          ><div class="d-flex align-center menu-progress" v-if="grammarProgressPct > 0">
            <v-progress-linear
              :model-value="grammarProgressPct"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ grammarProgressPct }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative  && [LanguageLevel.A1_A2, LanguageLevel.B1_B2].includes(langLevel)"
          prepend-icon="mdi-card-text-outline"
          :title="TRANS[langForeign].general.vocabulary + ' - ' + TRANS[langNative].general.vocabulary"
          :value="TRANS[langForeign].general.vocabulary"
          @click="() => trackMenuClick('vocabulary', () => openUrl(`/${langForeign}/vocabulary`))"
        ><div class="d-flex align-center menu-progress" v-if="vocabularyProgress > 0">
            <v-progress-linear
              :model-value="vocabularyProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ vocabularyProgress }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative  && [LanguageLevel.B1_B2, LanguageLevel.C1_C2].includes(langLevel)"
          prepend-icon="mdi-emoticon-lol-outline"
          :title="TRANS[langForeign].general.jokes + ' - ' + TRANS[langNative].general.jokes"
          :value="TRANS[langForeign].general.jokes"
          @click="() => trackMenuClick('jokes', () => openUrl(`/${langForeign}/jokes`))"
        ><div class="d-flex align-center menu-progress" v-if="jokesProgress > 0">
            <v-progress-linear
              :model-value="jokesProgress"
              color="green"
              :height="PROGRESS_HIGHT"
              rounded
              class="flex-grow-1"
            />
            <span class="text-caption ml-2">{{ jokesProgress }}%</span>
          </div></v-list-item>

        <v-list-item
          v-if="langForeign && langNative"
          prepend-icon="mdi-microphone-message"
          :title="
            TRANS[langForeign].general.transcription +
            ' - ' +
            TRANS[langNative].general.transcription
          "
          :value="TRANS[langForeign].general.transcription"
          @click="() => trackMenuClick('transcription', () => openUrl(`/${langForeign}/transcription`))"
        ></v-list-item>

        <v-list-item
          v-if="langForeign && langNative"
          prepend-icon="mdi-video-box"
          :title="
            TRANS[langForeign].general.video_object_detection +
            ' - ' +
            TRANS[langNative].general.video_object_detection
          "
          :value="TRANS[langForeign].general.video_object_detection"
          @click="
            () =>
              trackMenuClick('video_object_detection', () =>
                openUrl(`/${langForeign}/video-object-detection`)
              )
          "
        ></v-list-item>

        <v-list-item
          v-if="langForeign && langNative"
          prepend-icon="mdi-notebook-plus-outline"
          :title="
            TRANS[langForeign].custom_vocabulary.custom_vocabulary +
            ' - ' +
            TRANS[langNative].custom_vocabulary.custom_vocabulary
          "
          :value="TRANS[langForeign].custom_vocabulary.custom_vocabulary"
          @click="
            () => trackMenuClick('custom_vocabulary', () => openUrl(`/${langForeign}/custom-vocabulary`))
          "
        ></v-list-item>

        <v-divider class="mt-4" />
        <v-list-item
          v-if="langForeign && langNative"
          prepend-icon="mdi-translate-variant"
          :title="
            TRANS[langForeign].general.translate + ' - ' + TRANS[langNative].general.translate
          "
          :value="TRANS[langForeign].general.translate"
          @click="() => trackMenuClick('translate', openTranslateDialog)"
        ></v-list-item>

        <v-list-item
          v-if="langForeign && langNative"
          prepend-icon="mdi-robot-outline"
          :title="
            TRANS[langForeign].chat.language_teacher +
            ' - ' +
            TRANS[langNative].chat.language_teacher
          "
          :value="TRANS[langForeign].chat.language_teacher"
          @click="
            () =>
              trackMenuClick('language_teacher', () => {
                sendEvent(GAnalyticsEvent.OPEN_LANGUAGE_TEACHER, { source: 'side_menu' });
                teacherChatIsOpen = true;
                menuOpen = false;
              })
          "
        ></v-list-item>

        <v-divider
          class="mt-4"
          v-if="langForeign && [LanguageLevel.B1_B2, LanguageLevel.C1_C2].includes(langLevel)"
        />
        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.B1_B2, LanguageLevel.C1_C2].includes(langLevel)"
          prepend-icon="mdi-receipt-text-edit-outline"
          :title="TRANS[langForeign].special_vocabulary.special_vocabulary + ' - ' + TRANS[langNative].special_vocabulary.special_vocabulary"
          :value="TRANS[langForeign].special_vocabulary.special_vocabulary"
          @click="
            () => trackMenuClick('special_vocabulary', () => openUrl(`/${langForeign}/add-auto-text`))
          "
        ></v-list-item>
        <v-list-item
          v-if="langForeign && langNative && [LanguageLevel.B1_B2, LanguageLevel.C1_C2].includes(langLevel)"
          prepend-icon="mdi-content-copy"
          :title="TRANS[langForeign].custom.own_text + ' - ' + TRANS[langNative].custom.own_text"
          :value="TRANS[langForeign].custom.own_text"
          @click="() => trackMenuClick('own_text', () => openUrl(`/${langForeign}/custom`))"
        ></v-list-item>
        <v-divider class="mt-2" />
        <v-list-group :value="t('general.settings')" v-if="langForeign">
          <template v-slot:activator="{ props }">
            <v-list-item
              prepend-icon="mdi-cog"
              v-bind="props"
              :title="t('general.settings')"
            ></v-list-item>
          </template>

          <v-list-item
            prepend-icon="mdi-translate-variant"
            @click="() => trackMenuClick('settings_language', openLangSettings)"
            class="list-item"
          >
            <v-list-item-title>{{ t('general.language') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-podium"
            @click="() => trackMenuClick('settings_level', openLevelSettings)"
            class="list-item"
          >
            <v-list-item-title>{{ t('general.level') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-headphones"
            @click="() => trackMenuClick('settings_audio_video', openAudioVideoSettings)"
            class="list-item"
          >
            <v-list-item-title>{{ t('settings.audio_video') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-invert-colors"
            @click="() => trackMenuClick('settings_colors', openThemeSettings)"
            class="list-item"
          >
            <v-list-item-title>{{ t('settings.colors') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-cookie"
            @click="() => trackMenuClick('settings_cookies', openCookieSettings)"
            class="list-item"
          >
            <v-list-item-title>Cookies</v-list-item-title>
          </v-list-item>
        </v-list-group>
      </v-list>
    </div>
    <v-overlay class="overlay" v-model="menuOpen"></v-overlay>
  </div>

  <Dialog
    v-model="teacherChatIsOpen"
    :width="900"
    :title="t('chat.language_teacher')"
    icon="mdi-robot-outline"
    @close="() => (teacherChatIsOpen = false)"
  >
    <TeacherChat v-if="teacherChatIsOpen" />
  </Dialog>

  <Dialog
    v-model="translatorDialogIsOpen"
    :width="500"
    :title="t('general.translate')"
    @close="() => (translatorDialogIsOpen = false)"
  >
    <div class="mt-n4">
      <div class="d-flex align-center mb-6 mr-3">
        <v-text-field
          clearable
          hide-details
          variant="underlined"
          class=""
          color="primary"
          v-model="toTranslate"
          @keyup.enter="getTranslation"
          :label="
            t('general.what_you_want_to_translate', {
              from: langOptions.find((o) => o.value === _langForeignTrans)?.title ?? '',
              to: langOptions.find((o) => o.value === _langNativeTrans)?.title ?? ''
            })
          "
        >
        </v-text-field>
        <div class="d-flex align-center justify-end grab ml-6 mb-n6" @click="() => switchLangs()">
          <div class="flag-wrapper ml-2">
            <v-img
              :src="langOptions.find((o) => o.value === _langForeignTrans)?.flag"
              alt=""
              class="flag-icon"
            />
          </div>
          <v-icon icon="mdi-arrow-right-thin" size="12" />
          <div class="flag-wrapper">
            <v-img
              :src="langOptions.find((o) => o.value === _langNativeTrans)?.flag"
              alt=""
              class="flag-icon"
            />
          </div>
        </div>
      </div>
      <v-alert v-if="invalidWord" type="warning" variant="tonal" density="compact" class="mb-4">
        <div>{{ t('general.word_not_found', { word: invalidWord.word }) }}</div>
        <div v-if="invalidWord.similarWords.length > 0" class="mt-2">
          {{ t('general.similar_words_found') }}
          <div class="d-flex flex-wrap ga-2 mt-1">
            <v-chip
              v-for="similarWord in invalidWord.similarWords"
              :key="similarWord"
              size="small"
              color="primary"
              variant="outlined"
              @click="(event) => selectSimilarWord(similarWord, event)"
            >
              {{ similarWord }}
            </v-chip>
          </div>
        </div>
      </v-alert>
      <div class="d-flex justify-end">
        <v-btn
          @click="getTranslation"
          color="primary"
          :disabled="!toTranslate || checkingWord"
          :loading="checkingWord"
          size="small"
          >{{ t('general.translate') }}</v-btn
        >
      </div>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.overlay {
  z-index: $z-level-1 !important;
}
.menu {
  position: fixed;
  top: 0px;
  right: 0px;
  z-index: $z-level-10 !important;
  background-color: rgb(var(--v-theme-background));
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  padding-right: 2px;
  padding-bottom: 80px;
  width: 0px;
  overflow: hidden;
  &.active {
    width: 300px;
    max-width: 90vw;
    overflow: auto;
  }
  transition: width 0.3s;
}
.difficult-progress {
  width: 15px;
}
.menu-progress {
  margin-top: -4px;
}
.flag-icon {
  width: 11px;
}
</style>
